import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import indlusLogo from '../../../assets/indlusbynemala.png'
import { usePlatformAuth } from '../context/PlatformAuthContext'
import { getBackendError } from '../../../shared/utils/formErrors'

interface FormData {
    email: string
    password: string
}

export default function AdminLogin() {
    const { isAuthenticated, isLoading, login } = usePlatformAuth()
    const navigate = useNavigate()
    const location = useLocation()
    const [showPw, setShowPw] = useState(false)
    const [apiError, setApiError] = useState('')
    const [submitting, setSubmitting] = useState(false)

    const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
        defaultValues: { email: '', password: '' },
    })

    const from = (location.state as { from?: Location } | null)?.from?.pathname ?? '/tenants'

    if (!isLoading && isAuthenticated) {
        return <Navigate to={from} replace />
    }

    const onSubmit = async (data: FormData) => {
        setApiError('')
        setSubmitting(true)
        try {
            await login(data.email.trim(), data.password)
            navigate(from, { replace: true })
        } catch (err) {
            setApiError(getBackendError(err)?.message ?? 'Não foi possível iniciar sessão. Tente novamente.')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: 'background.default',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                gap: 3,
            }}
        >
            <Box component="img" src={indlusLogo} alt="Indlus" sx={{ height: 44, width: 'auto' }} />

            <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, border: '1px solid #E5E7EB', width: '100%', maxWidth: 400 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Administração da plataforma
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Acesso restrito ao operador da plataforma.
                </Typography>

                {apiError && (
                    <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{apiError}</Alert>
                )}

                <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <TextField
                        label="Email"
                        type="email"
                        autoComplete="username"
                        autoFocus
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        {...register('email', {
                            required: 'O email é obrigatório.',
                            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Insira um email válido.' },
                        })}
                    />
                    <TextField
                        label="Palavra-passe"
                        type={showPw ? 'text' : 'password'}
                        autoComplete="current-password"
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPw((v) => !v)} edge="end" aria-label={showPw ? 'Ocultar palavra-passe' : 'Mostrar palavra-passe'}>
                                            {showPw ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                        {...register('password', { required: 'A palavra-passe é obrigatória.' })}
                    />
                    <Button type="submit" variant="contained" size="large" fullWidth disabled={submitting}>
                        {submitting ? 'A entrar…' : 'Entrar'}
                    </Button>
                </Box>

                <Box sx={{ mt: 2.5, textAlign: 'center' }}>
                    <Link
                        to="/forgot-password"
                        style={{ color: '#64748B', textDecoration: 'none', fontSize: '0.875rem' }}
                    >
                        Esqueceu-se da palavra-passe?
                    </Link>
                </Box>
            </Paper>
        </Box>
    )
}
